/**
 * CWS-1650: the New Order Request membership status strip.
 *
 * Built from `refusal_detail` (svc-order utils/vendorRefusalDetail), which the backend
 * sends on create-order-request and stores on the LPR row for reloads. Two tones:
 *  - `inactive`: a refused member (Terminated, Expired, Canceled, Moved, ...).
 *  - `limit`: an active membership that has used its washes ("4 of 4 · Resets 1 Nov").
 *
 * Dates carry where they came from. DRB sends no status-change date, so "Ended" is the
 * night Way's sync saw the change (WAY SYNC); a canceled or expired plan ends on DRB's
 * paid-through date (DRB).
 *
 * Expired reads as a sentence (CWS-1726): "Expired. Unlimited Basic membership expired
 * on: 17 Aug DRB". Without DRB's date the sync date is labelled "Seen", never "expired
 * on", so the night of the import is not passed off as the expiry date.
 *
 * Terminated shows a reason chip ("FRAUD") only for a known `termination_reason` code
 * (Q15). Nothing sends one yet: DRB's roster has no reason, and svc-order's
 * buildVendorRefusalDetail allowlist and the status events table would both need the
 * field first. The payload reaches every tablet at the location, so free text never
 * becomes a chip; only the codes below map to fixed labels.
 *
 * CWS-1730: a third tone, `active`, gives an active member a status line (sheet Act-2,
 * Act-3, Act-4, Act-5, Act-6): on New Order Request when the lane did not auto-redeem, and
 * on the Active Subscription popup from `membership_status`. A plain active member gets no
 * line on that popup; it already shows the plan, its washes and the renewal.
 *
 * CWS-1792: on New Order Request a plain active member does get one ("Active – plan · 0 of 2
 * washes used · Active till 30 Dec DRB"). A shop with auto-redeem off sends every member
 * there, so without it nothing on the card said the member was active.
 */

/** Versions of `refusal_detail` this UI understands. */
const SUPPORTED_VERSION = 1;

export type MembershipStatusTone = 'inactive' | 'limit' | 'active';

export interface MembershipStatusDate {
  label: string;
  /**
   * Formatted by the template. A DRB date is a YYYY-MM-DD calendar day, which the date
   * pipe reads as that day on any clock; the others are ISO timestamps.
   */
  date: string;
  source: 'WAY' | 'WAY SYNC' | 'DRB';
  /** The status date itself (shown in the tone colour). */
  emphasis: boolean;
  /** CWS-1792: the day is in another year, so the chip names the year ("21 Nov 2080"). */
  showYear?: boolean;
}

export interface MembershipStatusStripView {
  tone: MembershipStatusTone;
  title: string;
  planName: string | null;
  /**
   * CWS-1792: the Way package the vendor plan is mapped to, on an active member's line.
   * Absent when the backend sent none, so the other strips keep their shape.
   */
  packageName?: string;
  /** Sentence shown in place of "– planName", e.g. "Unlimited Basic membership expired on:". */
  headline?: string;
  /** Termination reason chip, e.g. "FRAUD"; null when no reason was sent. */
  reasonChip: string | null;
  /** "4 of 4" for the wash limit. */
  usage: string | null;
  resetsOn: string | null;
  dates: MembershipStatusDate[];
  /** Which suggestion row to show under the strip, if any. */
  suggestion: 'limit' | null;
}

const WASH_LIMIT_REASON = 'wash_limit_reached';

/** Inactive reasons and their titles; anything not redeemable and not listed is "Not Active". */
const INACTIVE_TITLES: Readonly<Record<string, string>> = {
  terminated: 'Membership Terminated',
  canceled: 'Membership Canceled',
  transferred_out: 'Membership Moved to Another Plate',
  unpaid: 'Membership Unpaid',
  out_of_units: 'No Washes Left',
  absent_from_roster: 'Membership No Longer Listed',
  suspended: 'Membership On Hold',
  disabled: 'Membership On Hold',
};
const INACTIVE_FALLBACK_TITLE = 'Membership Not Active';

/** Expired reasons and their titles; the strip reads as a sentence (CWS-1726). */
const EXPIRED_TITLES: Readonly<Record<string, string>> = {
  expired: 'Expired.',
  expired_card_declined: 'Expired - Card Declined.',
};

/**
 * CWS-1727: decline variant codes (libs/core declineVariant) and their chip labels on a
 * declined expiry. A plain decline gets none: the title already says "Card Declined".
 */
const DECLINE_VARIANT_LABELS: ReadonlyMap<string, string> = new Map([
  ['card_expired', 'CARD EXPIRED'],
  ['category_1_decline', 'HARD DECLINE'],
]);

/** Termination reason codes and their chip labels; any other value shows no chip. */
const TERMINATION_REASON_LABELS: ReadonlyMap<string, string> = new Map([
  ['fraud', 'FRAUD'],
  ['chargeback', 'CHARGEBACK'],
  ['abuse', 'ABUSE'],
]);

/**
 * Statuses the gate lets wash (libs/core REDEEMABLE_REASONS + card warnings). A refusal
 * carrying one of these means the lane did not auto-redeem a member DRB still lists; the
 * card shows the active line when there is one (CWS-1730), no upsell, and the DRB lookup in
 * the Redeem row (CWS-1729).
 */
const REDEEMABLE_REASONS: ReadonlySet<string> = new Set([
  'active',
  'cancel_scheduled',
  'card_declined',
  'card_hard_declined',
]);

function asText(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || null;
}

function asIso(value: unknown): string | null {
  const text = asText(value);
  return text && !Number.isNaN(new Date(text).getTime()) ? text : null;
}

function asCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

function lastWashDate(detail: Record<string, unknown>): MembershipStatusDate[] {
  const lastWash = asIso(detail['last_wash_at']);
  return lastWash ? [{ label: 'Last wash', date: lastWash, source: 'WAY', emphasis: false }] : [];
}

/** "4 of 4" and the reset date, when the backend counted a finite allowance. */
function usageOf(detail: Record<string, unknown>): {
  usage: string | null;
  resetsOn: string | null;
} {
  const used = asCount(detail['washes_used']);
  const allowed = asCount(detail['washes_allowed']);
  return {
    usage: used !== null && allowed !== null ? `${used} of ${allowed}` : null,
    resetsOn: asIso(detail['resets_on']),
  };
}

function washLimitStrip(detail: Record<string, unknown>): MembershipStatusStripView {
  return {
    tone: 'limit',
    title: 'Wash Limit Reached',
    // The plan name is already the history row's title right under the strip.
    planName: null,
    reasonChip: null,
    ...usageOf(detail),
    dates: lastWashDate(detail),
    suggestion: 'limit',
  };
}

function terminationReasonLabel(detail: Record<string, unknown>): string | null {
  const code = asText(detail['termination_reason'])?.toLowerCase();
  return (code && TERMINATION_REASON_LABELS.get(code)) || null;
}

function declineVariantLabel(reason: string, detail: Record<string, unknown>): string | null {
  if (reason !== 'expired_card_declined') return null;
  const code = asText(detail['decline_variant']);
  return (code && DECLINE_VARIANT_LABELS.get(code)) || null;
}

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * DRB's end day. svc-order sends `paid_through_day`, read in the zone the import stored
 * the date in; a row saved before that field takes the UTC day of `paid_through`, the
 * day the import pinned when it had no shop zone.
 */
function drbDay(detail: Record<string, unknown>): string | null {
  const day = asText(detail['paid_through_day']);
  if (day && CALENDAR_DAY.test(day)) return day;
  const paidThrough = asIso(detail['paid_through']);
  return paidThrough ? new Date(paidThrough).toISOString().slice(0, 10) : null;
}

/**
 * The status date: DRB's end date when the backend sent one (svc-order sends `paid_through`
 * only for the libs/core ENDED_ON_RECHARGE_DATE_REASONS), else the night Way's sync saw
 * the status. Each source gets its own label.
 */
function statusDate(
  detail: Record<string, unknown>,
  labels: { drb: string; sync: string }
): MembershipStatusDate | null {
  const endDay = drbDay(detail);
  if (endDay) {
    return { label: labels.drb, date: endDay, source: 'DRB', emphasis: true };
  }
  const seenAt = asIso(detail['status_seen_at']);
  return seenAt ? { label: labels.sync, date: seenAt, source: 'WAY SYNC', emphasis: true } : null;
}

function withLastWash(
  date: MembershipStatusDate | null,
  detail: Record<string, unknown>
): MembershipStatusDate[] {
  return date ? [date, ...lastWashDate(detail)] : lastWashDate(detail);
}

function expiredStrip(reason: string, detail: Record<string, unknown>): MembershipStatusStripView {
  const planName = asText(detail['plan_name']);
  const date = statusDate(detail, { drb: '', sync: 'Seen' });
  const subject = planName ? `${planName} membership` : 'Membership';
  return {
    tone: 'inactive',
    title: EXPIRED_TITLES[reason],
    planName,
    // The full stop keeps "expired" from running into a "Seen <sync date>" chip.
    headline: date?.source === 'DRB' ? `${subject} expired on:` : `${subject} expired.`,
    reasonChip: declineVariantLabel(reason, detail),
    usage: null,
    resetsOn: null,
    dates: withLastWash(date, detail),
    suggestion: null,
  };
}

function inactiveStrip(reason: string, detail: Record<string, unknown>): MembershipStatusStripView {
  if (Object.hasOwn(EXPIRED_TITLES, reason)) {
    return expiredStrip(reason, detail);
  }
  const label = reason === 'transferred_out' ? 'Moved' : 'Ended';
  const date = statusDate(detail, { drb: label, sync: label });
  return {
    tone: 'inactive',
    title: INACTIVE_TITLES[reason] ?? INACTIVE_FALLBACK_TITLE,
    planName: asText(detail['plan_name']),
    reasonChip: reason === 'terminated' ? terminationReasonLabel(detail) : null,
    usage: null,
    resetsOn: null,
    dates: withLastWash(date, detail),
    suggestion: null,
  };
}

/** A scheduled cancel, or a cancel still inside its paid term on a redeemed popup. */
const DISCONTINUING_REASONS: ReadonlySet<string> = new Set(['cancel_scheduled', 'canceled']);
const CARD_DECLINE_REASONS: ReadonlySet<string> = new Set(['card_declined', 'card_hard_declined']);

function syncDate(
  detail: Record<string, unknown>,
  label: string,
  emphasis: boolean
): MembershipStatusDate | null {
  const seenAt = asIso(detail['status_seen_at']);
  return seenAt ? { label, date: seenAt, source: 'WAY SYNC', emphasis } : null;
}

function activeBase(
  title: string,
  detail: Record<string, unknown>,
  dates: (MembershipStatusDate | null)[]
): MembershipStatusStripView {
  const packageName = asText(detail['package_name']);
  return {
    tone: 'active',
    title,
    planName: asText(detail['plan_name']),
    ...(packageName ? { packageName } : {}),
    reasonChip: null,
    // CWS-1792: only New Order Request sends counts; the Active Subscription popup has its own.
    ...usageOf(detail),
    dates: [
      ...dates.filter((date): date is MembershipStatusDate => date !== null),
      ...lastWashDate(detail),
    ],
    suggestion: null,
  };
}

/**
 * "Active till" is DRB's recharge date (`active_till_day`); on a redeemed canceled plan it
 * is the paid-through day. "Canceled" is the night Way's sync saw it: DRB sends no date.
 */
function activeTillDay(reason: string, detail: Record<string, unknown>): string | null {
  const tillDay = asText(detail['active_till_day']);
  if (tillDay && CALENDAR_DAY.test(tillDay)) return tillDay;
  return reason === 'canceled' ? drbDay(detail) : null;
}

/** YYYY-MM-DD of `now` on the tablet's own clock, comparable with a DRB calendar day. */
function localDay(now: Date): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * CWS-1792: DRB's recharge day for a plain active member. A pass plan renews on it, so it
 * reads "Paid through", not "Active till" (which says the plan ends). A day already past
 * only means the sync has not seen the recharge yet, so it is left out rather than shown
 * as an end; a day in another year names the year.
 */
function paidThroughDate(detail: Record<string, unknown>, now: Date): MembershipStatusDate | null {
  const day = asText(detail['active_till_day']);
  const today = localDay(now);
  if (!day || !CALENDAR_DAY.test(day) || day < today) return null;
  const date: MembershipStatusDate = {
    label: 'Paid through',
    date: day,
    source: 'DRB',
    emphasis: true,
  };
  return day.slice(0, 4) === today.slice(0, 4) ? date : { ...date, showYear: true };
}

/** CWS-1792: a plain active member, with the DRB day the plan is paid through. */
function plainActiveStrip(detail: Record<string, unknown>, now: Date): MembershipStatusStripView {
  return activeBase('Active', detail, [paidThroughDate(detail, now)]);
}

function discontinuingStrip(
  reason: string,
  detail: Record<string, unknown>
): MembershipStatusStripView {
  const day = activeTillDay(reason, detail);
  const activeTill: MembershipStatusDate | null = day
    ? { label: 'Active till', date: day, source: 'DRB', emphasis: true }
    : null;
  return activeBase('Canceled - Discontinuing', detail, [
    activeTill,
    syncDate(detail, 'Canceled', !activeTill),
  ]);
}

/**
 * CWS-1828: an active plan's past-due title names the decline DRB reported. Both "Card
 * Declined" and "Card Expired" store card_declined; `decline_variant` tells them apart.
 */
function pastDueTitle(detail: Record<string, unknown>): string {
  return asText(detail['decline_variant']) === 'card_expired'
    ? 'Past Due - Card Expired'
    : 'Past Due - Card Declined';
}

function activeStrip(
  reason: string,
  detail: Record<string, unknown>
): MembershipStatusStripView | null {
  if (DISCONTINUING_REASONS.has(reason)) {
    return discontinuingStrip(reason, detail);
  }
  if (CARD_DECLINE_REASONS.has(reason)) {
    const strip = activeBase(pastDueTitle(detail), detail, [
      syncDate(detail, 'Payment failed', true),
    ]);
    return reason === 'card_hard_declined' ? { ...strip, reasonChip: 'HARD DECLINE' } : strip;
  }
  const previousPlate = asText(detail['previous_plate']);
  if (previousPlate) {
    const movedAt = asIso(detail['plate_changed_at']);
    return {
      ...activeBase('Plate Changed', detail, [
        movedAt ? { label: 'Moved', date: movedAt, source: 'WAY SYNC', emphasis: true } : null,
      ]),
      headline: `Membership moved from plate ${previousPlate}`,
    };
  }
  const variant = asText(detail['plan_variant']);
  if (variant === 'transfer_in') {
    return activeBase('Transferred In', detail, [syncDate(detail, 'Moved in', true)]);
  }
  if (variant === 'joined') {
    return activeBase('New Member', detail, [syncDate(detail, 'Joined', true)]);
  }
  return null;
}

function readDetail(detail: unknown): { row: Record<string, unknown>; reason: string } | null {
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
    return null;
  }
  const row = detail as Record<string, unknown>;
  if (row['version'] !== SUPPORTED_VERSION) {
    return null;
  }
  return { row, reason: asText(row['reason_code']) ?? 'unknown' };
}

/** The card's `refusal_detail`: the live payload, or the LPR row's snapshot on reload. */
export function refusalDetailOf(notification: { data?: unknown } | null | undefined): unknown {
  return (notification?.data as Record<string, unknown> | null | undefined)?.['refusal_detail'];
}

/**
 * CWS-1729: whether the refused member is one the gate lets wash. The lane only declined
 * to auto-redeem, so the card needs no upsell or "Check with DRB" banner.
 *
 * @param detail `refusal_detail` from the notification data
 */
export function isRedeemableRefusalDetail(detail: unknown): boolean {
  const read = readDetail(detail);
  return read !== null && REDEEMABLE_REASONS.has(read.reason);
}

/**
 * @param detail `refusal_detail` from the notification data (unknown shape: SSE or reload)
 * @param now the tablet's clock, for a plain active member's paid-through day
 * @returns the strip to show, or null to keep the card's existing sentence
 */
export function buildMembershipStatusStrip(
  detail: unknown,
  now: Date = new Date()
): MembershipStatusStripView | null {
  const read = readDetail(detail);
  if (!read) {
    return null;
  }
  const { row, reason } = read;
  if (reason === WASH_LIMIT_REASON) {
    return washLimitStrip(row);
  }
  if (REDEEMABLE_REASONS.has(reason)) {
    return activeStrip(reason, row) ?? plainActiveStrip(row, now);
  }
  return inactiveStrip(reason, row);
}

/**
 * CWS-1730: the line on the Active Subscription popup, from `membership_status`. The member
 * redeemed, so a canceled plan here is still inside its paid term (Discontinuing).
 *
 * @param detail `membership_status` from the notification data
 * @returns the active strip, or null when there is nothing beyond what the popup shows
 */
export function buildActiveMembershipStatusStrip(
  detail: unknown
): MembershipStatusStripView | null {
  const read = readDetail(detail);
  if (!read) {
    return null;
  }
  const { row, reason } = read;
  return REDEEMABLE_REASONS.has(reason) || reason === 'canceled' ? activeStrip(reason, row) : null;
}
